import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api, setAccessToken, setTokens, getAccessToken } from '../lib/api';
import type { User } from '@hongweibing-netdisk/shared';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  hydrate: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (u: User) => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: true,

      login: async (email, password) => {
        const data = await api.auth.login({ email, password });
        setTokens(data.accessToken, data.refreshToken);
        set({ user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken });
      },

      register: async (email, password, name) => {
        const data = await api.auth.register({ email, password, name });
        setTokens(data.accessToken, data.refreshToken);
        set({ user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken });
      },

      logout: async () => {
        try { await api.auth.logout(); } catch { /* ignore */ }
        setAccessToken(null);
        set({ user: null, accessToken: null, refreshToken: null });
      },

      refresh: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;
        try {
          const data = await api.auth.refresh(refreshToken);
          setTokens(data.accessToken, data.refreshToken);
          set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
          return true;
        } catch {
          setAccessToken(null);
          set({ user: null, accessToken: null, refreshToken: null });
          return false;
        }
      },

      hydrate: async () => {
        const { accessToken, refreshToken, user } = get();
        if (!accessToken && !getAccessToken()) {
          set({ loading: false });
          return;
        }
        if (user && accessToken) {
          set({ loading: false });
          return;
        }
        if (refreshToken) {
          const ok = await get().refresh();
          if (!ok) { set({ loading: false }); return; }
        }
        if (!getAccessToken()) { set({ loading: false }); return; }
        try {
          const me = await api.users.me();
          set({ user: me, loading: false });
        } catch {
          set({ loading: false });
        }
      },

      setUser: (u) => set({ user: u }),

      // 从服务器拉取最新用户信息（配额/用量等）
      refreshUser: async () => {
        if (!getAccessToken()) return;
        try {
          const me = await api.users.me();
          set({ user: me });
        } catch { /* ignore */ }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ accessToken: state.accessToken, refreshToken: state.refreshToken, user: state.user }),
      onRehydrateStorage: () => (state) => {
        void state?.hydrate();
      },
    }
  )
);

export function getAccessTokenValue(): string | null {
  return getAccessToken();
}

// API 层自动刷新令牌后，同步更新 store
if (typeof window !== 'undefined') {
  window.addEventListener('auth:refreshed', (e: Event) => {
    const detail = (e as CustomEvent).detail as { accessToken: string; refreshToken: string };
    if (detail?.accessToken) {
      useAuth.setState({ accessToken: detail.accessToken, refreshToken: detail.refreshToken });
    }
  });
}
